import { MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import DynamicGradient from "@/components/ui/DynamicGradient";
import ServiceAreaMap from "@/components/ServiceAreaMap";

// Service areas with links for SEO accessibility (city names don't translate)
const serviceAreaLinks = [
  { name: "The Woodlands", href: "/roofing-the-woodlands-tx" },
  { name: "Kingwood", href: "/roofing-kingwood-tx" },
  { name: "Humble", href: "/roofing-humble-tx" },
  { name: "Atascocita", href: "/roofing-atascocita-tx" },
  { name: "Spring", href: "/roofing-spring-tx" },
  { name: "Conroe", href: "/roofing-conroe-tx" },
  { name: "Aldine", href: "/roofing-aldine-tx" },
];

const LocalAuthoritySection = () => {
  const { t } = useTranslation('home');
  const [headerVisible, setHeaderVisible] = useState(false);
  const [mapVisible, setMapVisible] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const headerObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setHeaderVisible(true);
      },
      { threshold: 0.3 }
    );
    const mapObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setMapVisible(true);
      },
      { threshold: 0.1 }
    );
    if (headerRef.current) headerObserver.observe(headerRef.current);
    if (mapRef.current) mapObserver.observe(mapRef.current);
    return () => {
      headerObserver.disconnect();
      mapObserver.disconnect();
    };
  }, []);

  return (
    <DynamicGradient className="py-16 md:py-24 bg-background" variant="secondary">
      {/* Header - contained */}
      <div className="container mx-auto px-4">
        <div
          ref={headerRef}
          className={`text-center mb-10 md:mb-14 max-w-6xl mx-auto transition-all duration-1000 ease-out ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="section-eyebrow">{t('localAuthority.eyebrow')}</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-navy mb-3 md:mb-4">
            {t('localAuthority.headline')}
          </h2>
          <p
            className={`text-xl sm:text-2xl md:text-3xl font-heading text-charcoal mb-4 md:mb-6 transition-all duration-1000 ease-out delay-150 ${
              headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            {t('localAuthority.tagline')}
          </p>
          <p
            className={`text-base md:text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed transition-all duration-1000 ease-out delay-300 ${
              headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            {t('localAuthority.description')}
          </p>
        </div>
      </div>

      {/* Full-width Map Section */}
      <div ref={mapRef} className="relative mb-10 md:mb-14">
        <motion.div
          className="w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={mapVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <ServiceAreaMap />
        </motion.div>
      </div>

      {/* Text links for SEO and accessibility */}
      <div className="container mx-auto px-4">
        <motion.div
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-8">
            {serviceAreaLinks.map((area, index) => (
              <Link
                key={index}
                to={area.href}
                className="text-navy hover:text-primary font-medium text-sm md:text-base transition-colors inline-flex items-center gap-1 group"
              >
                {area.name}
                <ArrowRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </Link>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <Link
            to="/book-inspection"
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold px-6 md:px-8 py-3 md:py-4 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-lg text-sm md:text-base"
          >
            <MapPin className="w-4 h-4 md:w-5 md:h-5" />
            {t('localAuthority.button')}
          </Link>
          <p className="text-sm text-muted-foreground mt-4">
            {t('localAuthority.footer')}
          </p>
        </motion.div>
      </div>
    </DynamicGradient>
  );
};

export default LocalAuthoritySection;
